import {useCallback, useMemo} from 'react'
import {ActivityIndicator, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useFocusEffect} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {parseVideoIdFromRoute} from '#/lib/videos'
import {useYouTubeOEmbedQuery} from '#/state/queries/youtube-oembed'
import {useSetMinimalShellMode} from '#/state/shell'
import {atoms as a, useTheme} from '#/alf'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {VideoPlayer} from '#/components/VideoPlayer'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'VideoWatch'>

export function VideoWatchScreen({route}: Props) {
  const {v: routeVideoId} = route.params
  const {_} = useLingui()
  const t = useTheme()
  const setMinimalShellMode = useSetMinimalShellMode()

  useFocusEffect(
    useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  // Parse the prefixed video ID from the route (e.g., "yt-dQw4w9WgXcQ" or "short-abc123")
  const parsed = useMemo(
    () => parseVideoIdFromRoute(routeVideoId),
    [routeVideoId],
  )

  // Fetch video metadata from YouTube oEmbed API
  const {
    data: oembed,
    isLoading: isMetadataLoading,
    error,
  } = useYouTubeOEmbedQuery({
    videoId: parsed?.videoId,
    enabled: !!parsed,
  })

  // Invalid ID format
  if (!parsed) {
    return (
      <Layout.Screen testID="videoWatchScreen">
        <Layout.Center>
          <Layout.Header.Outer>
            <Layout.Header.BackButton />
          </Layout.Header.Outer>

          <View style={[a.flex_1, a.justify_center, a.align_center, a.p_xl]}>
            <Text style={[a.text_lg, t.atoms.text_contrast_medium]}>
              <Trans>Invalid video ID</Trans>
            </Text>
          </View>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  // Loading metadata
  if (isMetadataLoading) {
    return (
      <Layout.Screen testID="videoWatchScreen">
        <Layout.Center>
          <Layout.Header.Outer>
            <Layout.Header.BackButton />
          </Layout.Header.Outer>

          <View style={[a.flex_1, a.justify_center, a.align_center, a.p_xl]}>
            <ActivityIndicator size="large" color={t.palette.primary_500} />
          </View>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  // Error fetching metadata
  if (error) {
    return (
      <Layout.Screen testID="videoWatchScreen">
        <Layout.Center>
          <Layout.Header.Outer>
            <Layout.Header.BackButton />
          </Layout.Header.Outer>

          <View style={[a.flex_1, a.justify_center, a.align_center, a.p_xl]}>
            <Text style={[a.text_lg, t.atoms.text_contrast_medium]}>
              <Trans>Video not found</Trans>
            </Text>
          </View>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  const title = oembed?.title ?? _(msg`Video`)
  const authorName = oembed?.author_name

  return (
    <Layout.Screen testID="videoWatchScreen">
      <Layout.Center>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
        </Layout.Header.Outer>
      </Layout.Center>

      <Layout.Content>
        {/* Video Player with custom controls */}
        <VideoPlayer
          videoId={parsed.videoId}
          orientation={parsed.orientation}
        />

        {/* Video Info */}
        <View style={[a.px_lg, a.py_md, a.gap_xs]}>
          <Text style={[a.text_xl, a.font_bold, t.atoms.text]}>{title}</Text>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {authorName ? <>{authorName} • </> : null}
            {parsed.orientation === 'vertical' ? (
              <Trans>YouTube Short</Trans>
            ) : (
              <Trans>YouTube</Trans>
            )}
          </Text>
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}
